/**
 * V13.14.4 — Learner Profile hub.
 *
 * Single page, two tabs:
 *   1. **Profile**: name, email, phone, height (cm), pre-pregnancy weight (kg),
 *      EDD. Used by the Vault → Weight Tracker BMI calc + course gate's EDD logic.
 *   2. **Stage**: prenatal ↔ postnatal flip with delivery flow. Pulled out of
 *      Settings so all profile-shaped data lives in one place.
 *
 * Replaces the old single-purpose ProfilePage. The Settings page now points
 * here for the stage flip.
 */
import { TalkToTeamButton } from '../components/learner/TalkToTeamButton';
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Camera, User, BabyIcon, Calendar, Save, Phone, Pencil } from 'lucide-react';
import ChangePhoneDialog from '../components/ChangePhoneDialog';
import PurchasesRefunds from '../components/products/PurchasesRefunds';
import { Receipt } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

export default function ProfilePage() {
  const { user } = useAuth();
  const isLearner = user?.role === 'learner';
  // V14.1 — Postnatal context detection. Trust ANY signal so the UI flips
  // even if backend `pregnancy_profile.stage` hasn't been migrated yet.
  const profile = user?.pregnancy_profile || {};
  const isPostnatal = isLearner && (
    profile.stage === 'postnatal'
    || profile.course_type === 'postnatal'
    || !!profile.delivery_date
  );
  const [params, setParams] = useSearchParams();
  const initialTab = isLearner && ['stage', 'purchases'].includes(params.get('tab')) ? params.get('tab') : 'profile';
  const [tab, setTab] = useState(initialTab);

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <header>
        <h1 className="font-heading text-3xl">My Profile</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {!isLearner
            ? 'Manage your personal details.'
            : isPostnatal
            ? 'Manage your personal details and postnatal stage.'
            : 'Manage your personal details and pregnancy stage.'}
        </p>
      </header>

      {isLearner ? (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ tab: v }); }}>
          <TabsList className="grid w-full grid-cols-3 max-w-md" data-testid="profile-tabs">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="h-3.5 w-3.5 mr-1" />Profile
            </TabsTrigger>
            <TabsTrigger value="stage" data-testid="tab-stage">
              <BabyIcon className="h-3.5 w-3.5 mr-1" />Stage
            </TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases">
              <Receipt className="h-3.5 w-3.5 mr-1" />Purchases
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <ProfileTab isLearner={true} />
          </TabsContent>

          <TabsContent value="stage" className="mt-4">
            <StageTab />
          </TabsContent>

          <TabsContent value="purchases" className="mt-4">
            <PurchasesRefunds />
          </TabsContent>
        </Tabs>
      ) : (
        // Non-learners (instructor / admin / doctor) — single Profile view, no Stage tab,
        // no body details, no EDD. Just personal info + avatar.
        <ProfileTab isLearner={false} />
      )}
    </div>
  );
}

// ───────────────── Profile tab (personal + body details + EDD) ─────────────────
// V13.14.6 — Body details + EDD only render for learners. Instructors/admins see
// just avatar + personal info (name/email/phone). They have no need for stage,
// height, pre-pregnancy weight, or EDD.

function ProfileTab({ isLearner = true }) {
  const [user, setUser] = useState(null);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);

  const isInstructor = user?.role === 'instructor';

  const load = async () => {
    try {
      // Learners get /api/learner/me which includes pregnancy state.
      // Non-learners get /api/users/me — no pregnancy state, no EDD.
      if (isLearner) {
        const r = await api.get('/api/learner/me');
        setUser(r.data.user);
        setState(r.data.state);
        const eddIso = r.data.user?.pregnancy_profile?.edd || '';
        setForm({
          name: r.data.user.name || '',
          email: r.data.user.email || '',
          height_cm: r.data.user.height_cm ?? '',
          pre_pregnancy_weight_kg: r.data.user.pre_pregnancy_weight_kg ?? '',
          edd: eddIso ? eddIso.slice(0, 10) : '',
        });
      } else {
        const r = await api.get('/api/users/me');
        setUser(r.data);
        setState(null);
        setForm({
          name: r.data.name || '',
          email: r.data.email || '',
          // V14.1 — instructor-only editable fields (mirrors super-admin's modal)
          bio: r.data.bio || '',
          experience: r.data.experience || '',
          degrees: r.data.degrees || '',
          bank_name: r.data.bank_name || '',
          bank_account: r.data.bank_account || '',
          ifsc: r.data.ifsc || '',
          upi_id: r.data.upi_id || '',
          pan_number: r.data.pan_number || '',
        });
      }
    } catch {
      toast.error('Could not load profile');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
      };
      if (form.height_cm !== '') payload.height_cm = parseFloat(form.height_cm);
      if (form.pre_pregnancy_weight_kg !== '')
        payload.pre_pregnancy_weight_kg = parseFloat(form.pre_pregnancy_weight_kg);
      // V14.1 — instructor-editable fields (backend whitelists these for instructor role)
      if (user?.role === 'instructor') {
        ['bio', 'experience', 'degrees', 'bank_name', 'bank_account', 'ifsc', 'upi_id', 'pan_number'].forEach((k) => {
          if (form[k] !== undefined && form[k] !== null) payload[k] = form[k];
        });
      }
      // Only send EDD when the learner is allowed to edit AND it actually changed
      const originalEdd = (user?.pregnancy_profile?.edd || '').slice(0, 10);
      if (form.edd && form.edd !== originalEdd && !state?.edd_locked) {
        payload.edd = form.edd;
      }
      await api.put('/api/users/me', payload);
      toast.success('Profile saved');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save';
      toast.error(msg);
    }
    setSaving(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Please choose a photo under 3 MB'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/api/users/me/photo', fd);
      toast.success('Photo updated');
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || (typeof d === 'string' ? d : 'Upload failed'));
    }
    setPhotoUploading(false);
  };

  if (!user) return <Skeleton className="h-96 rounded-2xl" />;

  // V14.1 — Postnatal trumps "isPrenatal" for hiding pregnancy-only fields.
  // Body details (height + pre-pregnancy weight) are pregnancy-specific BMI
  // inputs — not relevant for postnatal users (Baby Tracker handles their
  // tracking instead).
  const profile = user.pregnancy_profile || {};
  const isPostnatal = isLearner && (
    profile.stage === 'postnatal'
    || profile.course_type === 'postnatal'
    || !!profile.delivery_date
  );
  const isPrenatal = !isPostnatal && (((user.pregnancy_profile?.stage || user.pregnancy_profile?.course_type) || 'prenatal') === 'prenatal');

  return (
    <div className="space-y-4">
      {/* Avatar block */}
      <Card className="rounded-2xl">
        <CardContent className="flex items-center gap-5 pt-5">
          <div className="relative">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.photo_url ? `/api/users/me/photo?v=${Date.now()}` : undefined} />
              <AvatarFallback className="bg-rose-100 text-rose-700 text-xl font-medium">
                {(user.name || 'U')[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <label className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-rose-500 text-white flex items-center justify-center cursor-pointer shadow-sm hover:bg-rose-600">
              {photoUploading ? '…' : <Camera className="h-3.5 w-3.5" />}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])}
                disabled={photoUploading} />
            </label>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            <Badge variant="outline" className="text-[10px] mt-1 capitalize">
              {isLearner ? (user.learner_type || 'free') : user.role}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Personal info */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Personal info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name" className="text-xs">Name</Label>
              <Input id="name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="profile-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="profile-email-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Phone</Label>
              <div className="flex gap-2">
                <Input value={user.phone || ''} disabled className="font-mono text-sm" />
                <Button variant="outline" size="sm" onClick={() => setPhoneOpen(true)}
                        data-testid="change-phone-btn">
                  <Phone className="h-3.5 w-3.5 mr-1" />Change
                </Button>
              </div>
            </div>
          </div>
          {!isLearner && (
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700"
                    data-testid="profile-save-btn">
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* V14.1 — Instructor profile fields (bio + experience + degrees) */}
      {!isLearner && user?.role === 'instructor' && (
        <Card className="rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading">Professional info</CardTitle>
            <p className="text-xs text-muted-foreground">Visible to learners on your batch and 1:1 booking pages.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="bio" className="text-xs">Short bio</Label>
              <Input id="bio" value={form.bio || ''}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="e.g. Certified Prenatal Yoga Instructor with 8+ years of experience"
                data-testid="profile-bio-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="experience" className="text-xs">Experience</Label>
                <Input id="experience" value={form.experience || ''}
                  onChange={(e) => setForm({ ...form, experience: e.target.value })}
                  placeholder="e.g. 5 years"
                  data-testid="profile-experience-input" />
              </div>
              <div>
                <Label htmlFor="degrees" className="text-xs">Degrees & certifications</Label>
                <Input id="degrees" value={form.degrees || ''}
                  onChange={(e) => setForm({ ...form, degrees: e.target.value })}
                  placeholder="e.g. RPYT-500, BPT"
                  data-testid="profile-degrees-input" />
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
              <Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save professional info'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* V14.1 — Instructor banking & tax (private, used for payouts) */}
      {!isLearner && user?.role === 'instructor' && (
        <Card className="rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading">Banking & tax</CardTitle>
            <p className="text-xs text-muted-foreground">
              Used by the finance team for payouts and TDS. Visible only to you and the super-admin.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bank_name" className="text-xs">Bank name</Label>
                <Input id="bank_name" value={form.bank_name || ''}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="e.g. HDFC Bank"
                  data-testid="profile-bank-name-input" />
              </div>
              <div>
                <Label htmlFor="bank_account" className="text-xs">Account number</Label>
                <Input id="bank_account" value={form.bank_account || ''}
                  onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                  placeholder="e.g. 1234567890"
                  data-testid="profile-bank-account-input" />
              </div>
              <div>
                <Label htmlFor="ifsc" className="text-xs">IFSC code</Label>
                <Input id="ifsc" value={form.ifsc || ''}
                  onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })}
                  placeholder="e.g. HDFC0001234"
                  data-testid="profile-ifsc-input" />
              </div>
              <div>
                <Label htmlFor="upi_id" className="text-xs">UPI ID (alternative)</Label>
                <Input id="upi_id" value={form.upi_id || ''}
                  onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                  placeholder="e.g. yourname@upi"
                  data-testid="profile-upi-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pan_number" className="text-xs">PAN number</Label>
                <Input id="pan_number" value={form.pan_number || ''}
                  onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
                  placeholder="e.g. ABCDE1234F"
                  className="font-mono"
                  data-testid="profile-pan-input" />
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
              <Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save banking info'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Body details + EDD (prenatal only) — V13.14.6: learner-only.
          V14.1: hidden entirely for postnatal users (height + pre-pregnancy
          weight are BMI inputs not used after delivery; Baby Tracker takes
          over the tracking). */}
      {isLearner && !isPostnatal && (
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Body details</CardTitle>
          <p className="text-xs text-muted-foreground">
            Used by the Weight Tracker BMI calc. {isPrenatal ? 'EDD also drives course unlocks.' : ''}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="height" className="text-xs">Height (cm)</Label>
              <Input id="height" type="number" min="100" max="220" step="0.5"
                value={form.height_cm}
                onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                placeholder="e.g. 162"
                data-testid="profile-height-input" />
            </div>
            <div>
              <Label htmlFor="prew" className="text-xs">Pre-pregnancy weight (kg)</Label>
              <Input id="prew" type="number" min="30" max="200" step="0.1"
                value={form.pre_pregnancy_weight_kg}
                onChange={(e) => setForm({ ...form, pre_pregnancy_weight_kg: e.target.value })}
                placeholder="e.g. 58"
                data-testid="profile-pre-weight-input" />
            </div>
            {isPrenatal && (
              <div>
                <Label htmlFor="edd" className="text-xs flex items-center gap-1">
                  Expected Due Date (EDD)
                  {state?.edd_locked && (
                    <span title="Locked after enrolment" className="text-amber-600">🔒</span>
                  )}
                </Label>
                <Input id="edd" type="date" value={form.edd}
                  onChange={(e) => setForm({ ...form, edd: e.target.value })}
                  disabled={state?.edd_locked}
                  data-testid="profile-edd-input" />
                {state?.edd_locked ? (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Locked {state.edd_source === 'ops_enrolment' ? 'after enrolment' :
                            state.edd_source === 'admin_override' ? 'by admin' : ''}.
                    Please contact our team if it needs to change.
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {state?.edd_edits_remaining ?? 1} self-edit
                    {(state?.edd_edits_remaining ?? 1) === 1 ? '' : 's'} remaining.
                  </p>
                )}
              </div>
            )}
          </div>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700"
                  data-testid="profile-save-btn">
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </CardContent>
      </Card>
      )}

      <ChangePhoneDialog open={phoneOpen} onClose={() => setPhoneOpen(false)}
        currentPhone={user.phone}
        onSaved={() => { setPhoneOpen(false); load(); }} />

      {/* V14.5 Part 19i — Change Password section. Shown to ALL profile
          users (learners, instructors, doctors, admins). Anyone who logged
          in with a WATI-issued temporary password (`password_is_temporary`)
          sees a coral banner nudging them to change it now. */}
      <ChangePasswordCard user={user} reload={load} />
    </div>
  );
}

// ───────────────── Change Password (V14.5 Part 19i, simplified V19j) ─────────────────
// V19j removed the "temp password" path — password reset now goes through
// the tokenized link flow (WhatsApp or email). So this card is purely for
// security-hygiene re-keying when an authenticated user wants to rotate.
function ChangePasswordCard({ user, reload }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const noPassword = !!user?.google_id && !user?.has_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      // Other devices are signed out; keep this one signed in with the fresh token.
      if (res?.data?.access_token) localStorage.setItem('ss_token', res.data.access_token);
      toast.success('Password updated. Use it next time you sign in.');
      setCurrent(''); setNext(''); setConfirm(''); setOpen(false);
      if (reload) reload();
    } catch (err) {
      const data = err?.response?.data || {};
      toast.error(data.detail || 'Could not update password.');
    }
    setSaving(false);
  };

  return (
    <Card className="rounded-2xl" data-testid="change-password-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Password</CardTitle>
      </CardHeader>
      <CardContent>
        {!open ? (
          <Button
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid="open-change-password-btn"
          >
            Change password
          </Button>
        ) : (
          <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="change-password-form">
            <div>
              <Label htmlFor="current-password" className="text-xs uppercase tracking-wide text-muted-foreground">Current password</Label>
              <Input id="current-password" type="password" autoComplete="current-password"
                value={current} onChange={(e) => setCurrent(e.target.value)} required
                data-testid="current-password-input" />
            </div>
            <div>
              <Label htmlFor="new-password" className="text-xs uppercase tracking-wide text-muted-foreground">New password</Label>
              <Input id="new-password" type="password" autoComplete="new-password"
                minLength={6}
                value={next} onChange={(e) => setNext(e.target.value)} required
                data-testid="new-password-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Minimum 6 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wide text-muted-foreground">Confirm new password</Label>
              <Input id="confirm-password" type="password" autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                data-testid="confirm-password-input" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="bg-rose-600 hover:bg-rose-700"
                      data-testid="submit-change-password-btn">
                {saving ? 'Updating…' : 'Update password'}
              </Button>
              <Button type="button" variant="ghost"
                      onClick={() => { setOpen(false); setCurrent(''); setNext(''); setConfirm(''); }}
                      data-testid="cancel-change-password-btn">
                Cancel
              </Button>
            </div>
            {noPassword && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                You originally signed up with Google. Use 'Forgot password' on the login screen to set a password instead.
              </p>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// ───────────────── Stage tab (prenatal ↔ postnatal flip) ─────────────────

function StageTab() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [edd, setEdd] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [babyName, setBabyName] = useState('');
  const [babyGender, setBabyGender] = useState('prefer_not_to_say');
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/api/learner/me').then((r) => {
      setState(r.data.state);
      setEdd((r.data.state?.edd || '').slice(0, 10));
    }).catch(() => {});
  };
  useEffect(load, []);

  const flipToPostnatal = async () => {
    if (!deliveryDate) return toast.error('Please enter delivery date');
    setSaving(true);
    try {
      await api.post('/api/learner/stage/flip', {
        new_stage: 'postnatal',
        delivery_date: deliveryDate,
        baby_name: babyName,
        baby_gender: babyGender,
      });
      toast.success("You're now in postnatal mode 💕");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not update stage';
      toast.error(msg);
    }
    setSaving(false);
  };

  const updateEdd = async () => {
    if (!edd) return toast.error('Please pick a date');
    setSaving(true);
    try {
      await api.post('/api/learner/stage/flip', { new_stage: 'prenatal', edd });
      toast.success('EDD updated');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save';
      toast.error(msg);
    }
    setSaving(false);
  };

  if (!state) return <Skeleton className="h-72 rounded-2xl" />;

  // V14.1 — Effective stage: trust auth user's profile signals as a fallback
  // for the case where /api/learner/me hasn't picked up the migration. If
  // ANY signal says postnatal, render the postnatal banner.
  const userProfile = user?.pregnancy_profile || {};
  const effectivePostnatal = (
    state.stage === 'postnatal'
    || userProfile.stage === 'postnatal'
    || userProfile.course_type === 'postnatal'
    || !!userProfile.delivery_date
  );
  const stage = effectivePostnatal ? 'postnatal' : state.stage;
  const flip = state.postnatal_flip || {};
  const eddLocked = state.edd_locked;
  // Pull delivery info from auth user (kept fresh by /api/users/me on profile load)
  const deliveryDateDisplay = userProfile.delivery_date
    ? new Date(userProfile.delivery_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  // V14.5 Part 19q — legacy 'natural' displayed as 'Vaginal birth' (founder
  // spec: only two values exist for users — Vaginal / C-section).
  const deliveryModeDisplay =
    (userProfile.delivery_mode === 'vaginal' || userProfile.delivery_mode === 'natural')
      ? 'Vaginal birth'
      : userProfile.delivery_mode === 'c_section' ? 'C-Section'
      : null;

  return (
    <Card className="rounded-2xl" data-testid="stage-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-heading">
          <BabyIcon className="h-5 w-5 text-rose-600" />
          {effectivePostnatal ? 'Postnatal stage' : 'Pregnancy stage'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm">Current stage</p>
          <Badge variant="secondary" className="capitalize" data-testid="current-stage-badge">
            {stage}
          </Badge>
        </div>
        {state.weeks_pregnant && !effectivePostnatal && (
          <p className="text-xs text-muted-foreground">
            Estimated week: {state.weeks_pregnant} · Trimester {state.current_trimester}
            {state.days_until_edd != null && ` · ${state.days_until_edd} days until EDD`}
          </p>
        )}
        <Separator />

        {!effectivePostnatal ? (
          <>
            <div>
              <Label htmlFor="edd2" className="flex items-center gap-1">
                Expected Due Date (EDD)
                {eddLocked && <span title="Locked" className="text-amber-600">🔒</span>}
              </Label>
              <div className="flex gap-2 mt-1">
                <Input id="edd2" type="date" value={edd}
                  disabled={eddLocked}
                  onChange={(e) => setEdd(e.target.value)} data-testid="stage-edd-input" />
                <Button onClick={updateEdd} disabled={saving || eddLocked}
                        data-testid="stage-save-edd-btn">
                  Save
                </Button>
              </div>
              {eddLocked && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Locked {state.edd_source === 'ops_enrolment' ? 'after enrolment' :
                          state.edd_source === 'admin_override' ? 'by admin' : ''}.
                  Contact support to request a change.
                </p>
              )}
            </div>
            <Separator />
            {flip.allowed ? (
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-4">
                <p className="font-medium mb-1 flex items-center gap-2">
                  <BabyIcon className="h-4 w-4" />Already delivered?
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Switch to postnatal mode to track baby milestones.
                </p>
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="dd2" className="text-xs">Delivery date</Label>
                    <Input id="dd2" type="date" value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      data-testid="stage-delivery-date-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Baby's name (optional)" value={babyName}
                      onChange={(e) => setBabyName(e.target.value)}
                      data-testid="stage-baby-name-input" />
                    <Select value={babyGender} onValueChange={setBabyGender}>
                      <SelectTrigger data-testid="stage-baby-gender-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boy">Boy</SelectItem>
                        <SelectItem value="girl">Girl</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={flipToPostnatal} disabled={saving || !deliveryDate}
                          className="w-full bg-rose-600 hover:bg-rose-700"
                          data-testid="flip-to-postnatal-btn">
                    Switch to postnatal mode
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm"
                   data-testid="postnatal-too-early-banner">
                <p className="font-medium mb-1 flex items-center gap-2">
                  <BabyIcon className="h-4 w-4 text-slate-500" />
                  Postnatal switch — coming soon
                </p>
                {flip.reason === 'EDD_NOT_SET' ? (
                  <p className="text-xs text-muted-foreground">
                    Set your EDD above first — we use it to calculate when to enable
                    the postnatal switch.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      You'll be able to switch to postnatal mode in
                      <strong className="text-rose-700"> {flip.days_until_available} day
                      {flip.days_until_available === 1 ? '' : 's'}</strong>
                      {' '}({flip.available_at && new Date(flip.available_at).toLocaleDateString()}),
                      which is 3 weeks before your EDD.
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Delivered earlier than expected? Request a callback and we&apos;ll switch you over right away.
                    </p>
                    <TalkToTeamButton category="support" plan="postnatal_programme" message="Delivered earlier than expected — please switch me to postnatal mode."
                      label="Request a callback" testId="early-delivery-callback-btn" />
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <p className="font-medium">You're in postnatal mode</p>
            <p className="text-xs text-muted-foreground mt-1">
              Track milestones, weight, and feeds in the Baby Tracker.
            </p>
            {/* V14.1 — surface the delivery snapshot since EDD no longer applies */}
            {(deliveryDateDisplay || deliveryModeDisplay) && (
              <div className="grid grid-cols-2 gap-2 mt-3" data-testid="postnatal-delivery-summary">
                {deliveryDateDisplay && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-emerald-100">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Delivery date</p>
                    <p className="text-sm font-medium">{deliveryDateDisplay}</p>
                  </div>
                )}
                {deliveryModeDisplay && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-emerald-100">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Delivery mode</p>
                    <p className="text-sm font-medium">{deliveryModeDisplay}</p>
                  </div>
                )}
              </div>
            )}
            <Button variant="outline" className="mt-3"
                    onClick={() => window.location.href = '/learner/baby-tracker'}
                    data-testid="open-baby-tracker-btn">
              <Calendar className="h-4 w-4 mr-2" />Open baby tracker
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
